import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { initial } from '@jungles/shared';
import { useSession } from '../app/session';

/** Общий каркас экранов: шапка, таб-бар, полоса джунглей (SPEC §2). */

const TABS = [
  { to: '/', label: 'Главная', icon: '/assets/ui/tab-home.webp' },
  { to: '/workouts', label: 'Тренировки', icon: '/assets/ui/tab-workouts.webp' },
  { to: '/nutrition', label: 'Питание', icon: '/assets/ui/tab-food.webp' },
  { to: '/arena', label: 'Арена', icon: '/assets/ui/tab-arena.webp' },
];

export function Screen({
  children,
  tabs = false,
  deep = false,
  wide = false,
}: {
  children: ReactNode;
  tabs?: boolean;
  deep?: boolean;
  wide?: boolean;
}) {
  const className = ['app', tabs && 'tabs', deep && 'deep', wide && 'wide']
    .filter(Boolean)
    .join(' ');
  return <div className={className}>{children}</div>;
}

export function Header({ showAvatar = true }: { showAvatar?: boolean }) {
  const { user } = useSession();
  const navigate = useNavigate();

  return (
    <div className="hd">
      <span className="brand">
        jungles<i> /</i>
      </span>
      <span className="grow" />
      {showAvatar && user ? (
        <button
          className="av"
          type="button"
          onClick={() => navigate('/profile')}
          aria-label="Профиль"
        >
          {user.photoUrl ? <img src={user.photoUrl} alt="" /> : initial(user.name)}
        </button>
      ) : null}
    </div>
  );
}

export function BackLink({ to, label }: { to: string | number; label: string }) {
  const navigate = useNavigate();
  return (
    <button
      className="back"
      type="button"
      onClick={() => (typeof to === 'number' ? navigate(to) : navigate(to))}
    >
      ← {label}
    </button>
  );
}

export function TabBar() {
  return (
    <nav className="nav" aria-label="Основная навигация">
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.to === '/'}
          className={({ isActive }) => (isActive ? 'on' : undefined)}
        >
          <img src={tab.icon} alt="" />
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Полоса джунглей: в потоке, `margin-top:auto` — контент никогда не перекрыт. */
export function JungleStrip() {
  return <img className="jstrip" src="/assets/ui/jungle-strip.webp" alt="" />;
}

export function Loader({ label = 'Загружаем…' }: { label?: string }) {
  return (
    <div className="loading">
      <div className="spinner" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="loading">
      <p className="mu" style={{ textAlign: 'center' }}>
        {message}
      </p>
      {onRetry ? (
        <button className="b" type="button" onClick={onRetry}>
          Попробовать ещё раз
        </button>
      ) : null}
    </div>
  );
}

export function PointsValue({ value, size = 'sm' }: { value: number; size?: 'sm' | 'md' }) {
  return (
    <span className="pts">
      {value.toLocaleString('ru-RU')}
      <img
        className={`pt-ic ${size === 'sm' ? 'sm' : ''}`}
        src="/assets/ui/points-diamond-bicep.webp"
        alt="очков"
      />
    </span>
  );
}
